import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import { masterDataService } from '../../services/toyService';
import CustomDataDialog from '../common/CustomDataDialog';

function CustomDataManager() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  
  const [customData, setCustomData] = useState({
    brands: [],
    series: [],
    types: [],
    scales: [],
    sources: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedDataType, setSelectedDataType] = useState('');

  const loadCustomData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await masterDataService.getCustomMasterData(currentUser.uid);
      setCustomData(data);
    } catch (error) {
      console.error('Error loading custom data:', error);
      setError(t('customData.loadError') || 'Failed to load custom data');
    } finally {
      setLoading(false);
    }
  }, [currentUser.uid, t]);

  useEffect(() => {
    if (currentUser?.uid) {
      loadCustomData();
    }
  }, [currentUser?.uid, loadCustomData]);

  const handleAddCustomData = (dataType) => {
    setSelectedDataType(dataType);
    setShowAddDialog(true);
  };

  const handleCustomDataConfirm = async (newValue) => {
    try {
      await masterDataService.addCustomMasterData(currentUser.uid, selectedDataType, newValue);
      await loadCustomData(); // Reload the data
    } catch (error) {
      console.error('Error adding custom data:', error);
      setError(t('customData.addError') || 'Failed to add custom data');
    }
  };

  const handleDeleteCustomData = async (dataType, value) => {
    try {
      await masterDataService.deleteCustomMasterData(currentUser.uid, dataType, value);
      await loadCustomData(); // Reload the data
    } catch (error) {
      console.error('Error deleting custom data:', error);
      setError(t('customData.deleteError') || 'Failed to delete custom data');
    }
  };

  const getDataTypeLabel = (dataType) => {
    const labels = {
      brands: t('common.brand') || 'Brand',
      series: t('common.series') || 'Series',
      types: t('common.type') || 'Type',
      scales: t('common.scale') || 'Scale',
      sources: t('common.source') || 'Source'
    };
    return labels[dataType] || dataType;
  };

  const getDataTypeColor = (dataType) => {
    const colors = {
      brands: 'primary',
      series: 'secondary',
      types: 'success',
      scales: 'warning',
      sources: 'info'
    };
    return colors[dataType] || 'default';
  };

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper sx={{ p: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/settings')}
            sx={{ mb: 2 }}
          >
            {t('common.back') || 'Back to Settings'}
          </Button>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
            {t('customData.manageTitle') || 'Manage Custom Data'}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('customData.manageDescription') || 'Manage your personal brand, series, type, scale, and source entries. These are only visible to you.'}
          </Typography>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Custom Data Cards */}
        <Grid container spacing={3}>
          {Object.entries(customData).map(([dataType, values]) => (
            <Grid item xs={12} md={6} key={dataType}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" component="h2">
                      {getDataTypeLabel(dataType)}
                    </Typography>
                    <Chip 
                      label={`${values.length} ${t('customData.items') || 'items'}`}
                      color={getDataTypeColor(dataType)}
                      size="small"
                    />
                  </Box>
                  
                  {values.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      {t('customData.noItems', { type: getDataTypeLabel(dataType) }) || `No custom ${getDataTypeLabel(dataType).toLowerCase()} items yet.`}
                    </Typography>
                  ) : (
                    <List dense>
                      {values.map((value, index) => (
                        <React.Fragment key={value}>
                          <ListItem>
                            <ListItemText 
                              primary={value}
                              secondary={t('customData.customItem') || 'Custom item'}
                            />
                            <ListItemSecondaryAction>
                              <IconButton
                                edge="end"
                                aria-label={t('customData.delete') || 'Delete'}
                                onClick={() => handleDeleteCustomData(dataType, value)}
                                color="error"
                                size="small"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                          {index < values.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </CardContent>
                <CardActions>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => handleAddCustomData(dataType)}
                    variant="outlined"
                    size="small"
                    color={getDataTypeColor(dataType)}
                  >
                    {t('customData.addNew', { type: getDataTypeLabel(dataType) }) || `Add New ${getDataTypeLabel(dataType)}`}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Add Custom Data Dialog */}
        <CustomDataDialog
          open={showAddDialog}
          onClose={() => setShowAddDialog(false)}
          onConfirm={handleCustomDataConfirm}
          dataType={selectedDataType}
          title={t('customData.addNew', { type: getDataTypeLabel(selectedDataType) }) || `Add New ${getDataTypeLabel(selectedDataType)}`}
        />
      </Paper>
    </Container>
  );
}

export default CustomDataManager;
